import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { spindleApi, containersApi } from '../../../shared/lib/api'
import { useAuthStore } from '../../../shared/store/authStore'
import TabletHeader from '../../../shared/components/TabletHeader'
import TabletScreen from '../../../shared/components/TabletScreen'
import ReasonPicker from '../../../shared/components/ReasonPicker'
import {
  StepBar, FlowNote, InfoCard, BigButton, ScanZone,
  VarianceAlert, SuccessScreen, SectionHeading, Divider, LoadingSpinner,
} from '../../../shared/components/UI'

const STEPS = ['Operator QR', 'Container QR', 'Confirm']

const CLARIFY_REASONS = [
  'Machine tension issue',
  'Yarn breakage during process',
  'Operator error',
  'Power interruption',
  'Other (specify below)',
]

export default function OutputPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()

  const [step, setStep]           = useState(0)
  const [opQrInput, setOpQrInput] = useState('')
  const [loadingContext, setLoadingContext] = useState(null)
  const [scanningOp, setScanningOp] = useState(false)

  const [containerInput, setContainerInput] = useState('')
  const [outputContainer, setOutputContainer] = useState(null)
  const [scanningContainer, setScanningContainer] = useState(false)

  const [outputWeight, setOutputWeight]     = useState('')
  const [rollCount, setRollCount]           = useState('')
  
  const [resultData, setResultData]         = useState(null)
  const [viewState, setViewState]           = useState('form') // 'form' | 'ok' | 'low' | 'low-done'
  
  const [clarifyReason, setClarifyReason]   = useState('')
  const [clarifyNote, setClarifyNote]       = useState('')
  const [error, setError]                   = useState('')

  const handleScanOperator = () => {
    if (!opQrInput.trim()) return
    setScanningOp(true)
    setError('')
    spindleApi.scanOperatorQr(opQrInput.trim())
      .then(res => {
        setLoadingContext(res.data.data)
        setStep(1)
      })
      .catch(err => setError(err.response?.data?.message || 'Operator QR not found or already processed'))
      .finally(() => setScanningOp(false))
  }

  const handleScanContainer = () => {
    if (!containerInput.trim()) return
    setScanningContainer(true)
    setError('')
    containersApi.scan(containerInput.trim())
      .then(res => {
        setOutputContainer(res.data.data)
        setStep(2)
      })
      .catch(err => setError(err.response?.data?.message || 'Container not found'))
      .finally(() => setScanningContainer(false))
  }

  const { mutate, isPending } = useMutation({
    mutationFn: (clarification = {}) => spindleApi.recordOutput({
      spindleLoadingId: loadingContext.id,
      outputContainerId: outputContainer.id,
      actualOutputKg: parseFloat(outputWeight),
      jariRollCount: parseInt(rollCount),
      ...clarification
    }),
    onSuccess: (res) => {
      const data = res.data.data
      setResultData(data)
      if (data.isBelowTarget && !clarifyReason) {
        setViewState('low')
      } else {
        setViewState(data.isBelowTarget ? 'low-done' : 'ok')
      }
    },
    onError: (err) => setError(err.response?.data?.message || 'Failed to record output')
  })

  const handleSave = () => {
    if (!outputWeight || !rollCount || parseFloat(outputWeight) <= 0) return
    mutate()
  }

  const handleClarifySubmit = () => {
    if (!clarifyReason) return
    mutate({ clarificationReason: clarifyReason, clarificationNote: clarifyNote })
  }

  if (viewState === 'ok') return (
    <TabletScreen>
      <TabletHeader title="Spindle Output" role="Loading Supervisor" unit={user?.unitName || 'Unit'} teal />
      <div className="p-4">
        <SuccessScreen title="Output recorded" subtitle="Target achieved · Spindle output saved" />
        <div className="grid grid-cols-2 gap-3 mb-4">
          {[
            { label: 'Batch',         value: loadingContext?.batch?.batch_number,   color: 'text-brand-600' },
            { label: 'Operator',      value: loadingContext?.operator?.name },
            { label: 'Bobbin input',  value: `${loadingContext?.batch?.total_weight_kg} KG` },
            { label: `Yarn (${resultData?.output?.yarn_pct}%)`, value: `${resultData?.output?.yarn_added_kg} KG`, color: 'text-brand-600' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
              <p className="text-xs text-gray-400 mb-1">{label}</p>
              <p className={`text-base font-semibold text-gray-800 ${color || ''}`}>{value}</p>
            </div>
          ))}
        </div>
        <InfoCard rows={[
          { label: 'Expected output',     value: `${parseFloat(resultData?.output?.expected_output_kg).toFixed(2)} KG` },
          { label: 'Actual output',       value: `${parseFloat(resultData?.output?.actual_output_kg).toFixed(2)} KG`,  valueVariant: 'green' },
          { label: 'Output container',    value: outputContainer?.container_id, valueVariant: 'blue' },
          { label: 'Variance from target', value: `${resultData?.variancePct}%`, valueVariant: 'green' },
        ]} />
        <BigButton variant="primary" onClick={() => navigate('/app/dashboard')}>Back to Dashboard</BigButton>
      </div>
    </TabletScreen>
  )

  if (viewState === 'low') return (
    <TabletScreen>
      <TabletHeader title="Output Below Target" role="Loading Supervisor" unit={user?.unit?.name || 'Unit'} teal />
      <div className="p-4">
        <VarianceAlert pct={`${resultData?.variancePct}%`} expectedKg={resultData?.output?.expected_output_kg} actualKg={resultData?.output?.actual_output_kg}>
          <div />
        </VarianceAlert>
        <div className="flow-note-amber mb-4">
          Admin has been notified. Please provide a reason to continue.
        </div>
        <SectionHeading>Reason for low output</SectionHeading>
        <div className="mb-4">
          <ReasonPicker reasons={CLARIFY_REASONS} onChange={setClarifyReason} />
        </div>
        <div className="mb-4">
          <label className="form-label">Additional notes (optional)</label>
          <textarea className="form-input" rows={3} placeholder="Describe what happened…" value={clarifyNote} onChange={(e) => setClarifyNote(e.target.value)} />
        </div>
        {error && <p className="text-red-500 text-xs mb-3">{error}</p>}
        <BigButton variant="danger" disabled={!clarifyReason || isPending} onClick={handleClarifySubmit}>
          {isPending ? 'Submitting...' : 'Submit & Notify Admin'}
        </BigButton>
      </div>
    </TabletScreen>
  )

  if (viewState === 'low-done') return (
    <TabletScreen>
      <TabletHeader title="Spindle Output" role="Loading Supervisor" unit={user?.unit?.name || 'Unit'} teal />
      <div className="p-4">
        <SuccessScreen title="Output saved with flag" subtitle="Admin notified · Reason logged" iconColor="amber" />
        <div className="grid grid-cols-2 gap-3 mb-4">
          {[
            { label: 'Bobbin input',    value: `${resultData?.output?.input_weight_kg} KG` },
            { label: `Yarn (${resultData?.output?.yarn_pct}%)`, value: `${resultData?.output?.yarn_added_kg} KG` },
            { label: 'Expected output', value: `${parseFloat(resultData?.output?.expected_output_kg).toFixed(2)} KG` },
            { label: 'Actual output',   value: `${parseFloat(resultData?.output?.actual_output_kg).toFixed(2)} KG`, color: 'text-red-600' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
              <p className="text-xs text-gray-400 mb-1">{label}</p>
              <p className={`text-base font-semibold ${color || 'text-gray-800'}`}>{value}</p>
            </div>
          ))}
        </div>
        <div className="info-card mb-5" style={{ background: '#FEF3C7', borderColor: '#FCD34D' }}>
          <div className="info-row" style={{ borderBottom: 'none' }}>
            <span className="info-label">Shortfall</span>
            <span className="info-value" style={{ color: '#B45309' }}>
              {(resultData?.output?.expected_output_kg - resultData?.output?.actual_output_kg).toFixed(2)} KG · {Math.abs(resultData?.variancePct)}% below target
            </span>
          </div>
        </div>
        <BigButton variant="primary" onClick={() => navigate('/app/dashboard')}>Back to Dashboard</BigButton>
      </div>
    </TabletScreen>
  )

  return (
    <TabletScreen>
      <TabletHeader title="Spindle Output" role="Loading Supervisor" unit={user?.unit?.name || 'Unit'} teal />
      <div className="p-4">
        <StepBar steps={STEPS} current={step} />

        {/* Step 0 — Scan Operator QR */}
        {step === 0 && (
          <>
            <ScanZone teal title="Scan Operator QR" subtitle="Scan the QR given to operator at loading" />
            <div className="flex gap-2">
              <input className="form-input flex-1 text-sm" placeholder="Or type operator QR ID" value={opQrInput} onChange={(e) => setOpQrInput(e.target.value)} />
              <button disabled={scanningOp} onClick={handleScanOperator} className="bg-teal-600 text-white px-4 rounded-lg text-sm font-medium hover:bg-teal-700">
                {scanningOp ? '...' : 'Load'}
              </button>
            </div>
            {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
          </>
        )}

        {/* Step 1 — Scan Output Container QR */}
        {step === 1 && (
          <>
            <FlowNote variant="teal">Operator QR matched · {loadingContext?.batch?.batch_number} · {loadingContext?.operator?.name}</FlowNote>
            <InfoCard rows={[
              { label: 'Batch',          value: loadingContext?.batch?.batch_number, valueVariant: 'blue' },
              { label: 'Operator',       value: loadingContext?.operator?.name },
              { label: 'Shade',          value: loadingContext?.batch?.shade?.name },
              { label: 'Bobbins handed', value: loadingContext?.bobbins_loaded },
            ]} />
            <ScanZone teal title="Scan Output Container QR" subtitle="Container where operator loaded the finished jari" />
            <div className="flex gap-2">
              <input className="form-input flex-1 text-sm" placeholder="Or type container ID" value={containerInput} onChange={(e) => setContainerInput(e.target.value)} />
              <button disabled={scanningContainer} onClick={handleScanContainer} className="bg-teal-600 text-white px-4 rounded-lg text-sm font-medium hover:bg-teal-700">
                {scanningContainer ? '...' : 'Confirm'}
              </button>
            </div>
            {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
            <BigButton variant="secondary" onClick={() => setStep(0)} className="mt-4">Back</BigButton>
          </>
        )}

        {/* Step 2 — Enter weight & confirm */}
        {step === 2 && (
          <>
            <InfoCard rows={[
              { label: 'Batch',            value: loadingContext?.batch?.batch_number,                  valueVariant: 'blue' },
              { label: 'Operator',         value: loadingContext?.operator?.name },
              { label: 'Input weight',     value: `${loadingContext?.batch?.total_weight_kg} KG` },
              { label: 'Output container', value: outputContainer?.container_id,             valueVariant: 'blue' },
            ]} />
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="form-label">No. of jari rolls</label>
                <input
                  type="number" inputMode="numeric"
                  className="form-input text-2xl text-center py-4 font-semibold"
                  placeholder="0" min="1"
                  value={rollCount}
                  onChange={(e) => setRollCount(e.target.value)}
                />
              </div>
              <div>
                <label className="form-label">Output weight (KG)</label>
                <input
                  type="number" inputMode="decimal"
                  className="form-input text-2xl text-center py-4 font-semibold"
                  placeholder="0.0" step="0.1"
                  value={outputWeight}
                  onChange={(e) => setOutputWeight(e.target.value)}
                />
              </div>
            </div>
            {error && <p className="text-red-500 text-xs mb-3">{error}</p>}
            <BigButton variant="success" onClick={handleSave} disabled={!outputWeight || !rollCount || isPending}>
              {isPending ? 'Saving…' : 'Save & Close'}
            </BigButton>
            <BigButton variant="secondary" onClick={() => setStep(1)} className="mt-2">Back</BigButton>
          </>
        )}
      </div>
    </TabletScreen>
  )
}
